import { useState, useEffect } from 'react';
import QuickBooksService from '../../../services/quickbooksService';
import useImportStream from '../../../hooks/useImportStream';

export const useQuickBooksIntegration = (
  userProfile,
  currentUser,
  showNotification,
  handleNewCustomers
) => {
  const [isQbConnected, setIsQbConnected] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });

  // Initialize SSE hook for real-time updates
  const {
    isConnected: isSSEConnected,
    connectionError: sseError,
    importStatus,
    newRecords,
    importProgress: sseProgress,
    connectToImportStream,
    disconnectFromImportStream,
    clearNewRecords,
    isImporting: isSSEImporting,
    isCompleted: isSSECompleted
  } = useImportStream();

  // Get franchisee ID for QuickBooks operations
  const getFranchiseeId = () => {
    if (userProfile?.role === 'franchisee') {
      return userProfile.id;
    } else if (userProfile?.role === 'technician' && (userProfile?.franchiseeId || userProfile?.assignedFranchiseeId)) {
      return userProfile.franchiseeId || userProfile.assignedFranchiseeId;
    } else if (userProfile?.role === 'admin') {
      return userProfile.id;
    }
    return null;
  };

  // Check QuickBooks connection status
  const checkQuickBooksConnection = async () => {
    try {
      const franchiseeId = getFranchiseeId();
      
      console.log('QB Connection Check - franchiseeId:', franchiseeId, 'userProfile:', userProfile?.role, 'currentUser:', currentUser);
      
      if (franchiseeId) {
        const hasConnection = await QuickBooksService.hasConnection(franchiseeId);
        setIsQbConnected(hasConnection);
      } else {
        setIsQbConnected(false);
      }
    } catch (error) {
      console.error('Error checking QuickBooks connection:', error);
      setIsQbConnected(false);
    }
  };

  // Handle QuickBooks customer import
  const handleImportFromQuickBooks = async () => {
    setIsImporting(true);
    
    try {
      const franchiseeId = getFranchiseeId();
      
      if (!franchiseeId) {
        throw new Error('No franchisee ID found for import');
      }

      console.log('📡 Starting QuickBooks customer import with real-time updates...');
      
      // Connect to SSE stream for real-time updates
      connectToImportStream(
        franchiseeId, 
        'customers',
        // Progress callback (backward compatibility)
        (current, total) => {
          setImportProgress({ current, total });
          if (total === 1 && current === 0) {
            console.log('Getting QuickBooks customer count...');
          } else if (total > 1) {
            console.log(`Processing ${current}/${total} customers...`);
          }
        },
        // New records callback (real-time updates)
        handleNewCustomers
      );
      
      // Give SSE connection a moment to establish
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Start the import process
      const importResult = await QuickBooksService.importCustomers(franchiseeId, userProfile.id);
      console.log('📡 Import API call completed:', importResult);
      
    } catch (error) {
      console.error('Error importing customers:', error);
      showNotification(`Failed to import customers: ${error.message}`, 'error');
      setIsImporting(false);
      setImportProgress({ current: 0, total: 0 });
      disconnectFromImportStream();
    }
  };

  // Handle SSE import completion
  const handleImportComplete = () => {
    setIsImporting(false);
    setImportProgress({ current: 0, total: 0 });
    return isSSECompleted;
  };

  // Handle SSE errors
  useEffect(() => {
    if (sseError) {
      console.error('SSE connection error:', sseError);
      showNotification(`Connection error: ${sseError}`, 'error');
    }
  }, [sseError, showNotification]);

  return {
    isQbConnected,
    isImporting,
    importProgress,
    isSSEConnected,
    sseError,
    importStatus,
    newRecords,
    sseProgress,
    isSSEImporting,
    isSSECompleted,
    checkQuickBooksConnection,
    handleImportFromQuickBooks,
    handleImportComplete,
    clearNewRecords,
    disconnectFromImportStream
  };
};